/*
 * @Description: How to use section for AI Video Editor landing page
 */
import Image from 'next/image';
import React from 'react';

const CDN_BASE = 'https://assets.falcocut.ai/ai-video-editor';

interface StepData {
  number: number;
  title: string;
  description: string;
}

const steps: StepData[] = [
  {
    number: 1,
    title: 'Upload Your Video',
    description: 'Upload your video(MP4/MOV) to FalcoCut to begin editing.',
  },
  {
    number: 2,
    title: 'Describe the Edit You Want',
    description:
      'Tell FalcoCut what to do with simple text commands. The AI instantly applies your instructions without manual timeline work.',
  },
  {
    number: 3,
    title: 'Add a Reference Image',
    description:
      'Upload a photo or pick one from your asset library to guide the look of your video.',
  },
  {
    number: 4,
    title: 'Generate, Preview, and Export',
    description:
      'Click Start to let FalcoCut process your edits. Review the preview, make quick adjustments if needed, and export your finished video.',
  },
];

const StepItem: React.FC<StepData> = ({ number, title, description }) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#7C3AED] flex items-center justify-center text-white font-semibold text-sm">
      {number}
    </div>
    <div className="flex flex-col gap-2">
      <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
      <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
    </div>
  </div>
);

const HowToUseSection: React.FC = () => {
  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-10 pad:mb-14 sm:mb-16 leading-tight">
          How to Edit a Video with FalcoCut
        </h2>

        <div className="flex flex-col pad:flex-row gap-8 pad:gap-12 sm:gap-16 items-center">
          <div className="w-full pad:w-1/2">
            <div className="relative rounded-2xl overflow-hidden shadow-lg bg-[#F8F9FC]">
              <Image
                src={`${CDN_BASE}/how-to-edit-a-video-with-falcocut.png`}
                alt="FalcoCut AI Video Editor Interface"
                width={560}
                height={315}
                className="w-full h-auto object-contain"
              />
            </div>
          </div>

          <div className="w-full pad:w-1/2 flex flex-col gap-6 pad:gap-8">
            {steps.map((step) => (
              <StepItem key={step.number} {...step} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowToUseSection;
